import {
  Button,
  CircularProgress,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type Table as ReactTable,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import type React from 'react';
import { useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useAsyncFn } from 'react-use';
import { useSnapshot } from 'valtio';
import {
  addDigitizationAccess as apiAddAccess,
  searchOrganizations as apiSearchOrgs,
  searchProjects as apiSearchProjects,
} from '~/api/civilsearch';
import type { OrganizationDetail, Project } from '~/common/types';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import Table from '~/components2/Table';
import store from '../../../store';
import { orgColumns, projectColumns } from './columns';

type TFormValuesProject = { id: string; projectId: string; name: string };
type TFormValuesOrg = { id: string; name: string };

type TQuery = ReturnType<typeof useAsyncFn>[0];

function getTableProps(): Omit<TableOptions<any>, 'data' | 'columns'> {
  return {
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getRowId: (row) => row.id,
  };
}

const getMessage = (s: string) => `An error occurred while querying ${s}`;

export default function AddAccess() {
  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [orgs, setOrgs] = useState<OrganizationDetail[]>([]);
  const { agencyId } = useParams({ strict: false }) as { agencyId: string };
  const { agency } = useSnapshot(store) as typeof store;

  const projectsForm = useForm<TFormValuesProject>({
    defaultValues: { id: '', name: '', projectId: '' },
  });

  const orgsForm = useForm<TFormValuesOrg>({
    defaultValues: { id: '', name: '' },
  });

  const [projectsQuery, queryProjects] = useAsyncFn(
    async (data: TFormValuesProject) => {
      setProjects(await apiSearchProjects(data));
    },
    [agency?.digitization],
  );

  const [orgsQuery, queryOrgs] = useAsyncFn(
    async (data: TFormValuesOrg) => {
      setOrgs(await apiSearchOrgs(data));
    },
    [agency?.digitization],
  );

  const projectsTable = useReactTable({
    ...getTableProps(),
    data: projects,
    columns: projectColumns,
  });

  const orgsTable = useReactTable({
    ...getTableProps(),
    data: orgs,
    columns: orgColumns,
  });

  const onSearchProjects: SubmitHandler<TFormValuesProject> = async (data) => {
    try {
      await queryProjects(data);
      projectsForm.reset({}, { keepValues: true });
    } catch (e) {
      console.error('Error querying projects: ', e);
      Notify.error({ message: getMessage('projects') });
    }
  };

  const onSearchOrgs: SubmitHandler<TFormValuesOrg> = async (data) => {
    try {
      await queryOrgs(data);
      orgsForm.reset({}, { keepValues: true });
    } catch (e) {
      console.error('Error querying orgs: ', e);
      Notify.error({ message: getMessage('organizations') });
    }
  };

  const onSubmit = async () => {
    try {
      const projectIds = Object.keys(projectsTable.getSelectedRowModel().rowsById);
      const organizationIds = Object.keys(orgsTable.getSelectedRowModel().rowsById);

      if (isEmpty(projectIds) && isEmpty(organizationIds)) return;

      setIsProcessing(true);

      const res = await apiAddAccess(agencyId, projectIds, organizationIds);
      if (!res.id) throw new Error('Bad response!');
      if (!store.agency) throw new Error('Missing agency in store!');

      // optimistically update the store
      store.agency.digitization = res.digitization;

      Notify.info({
        message: `Digitization access added for ${projectIds.length} project(s) and ${organizationIds.length} organization(s)`,
      });

      setOpen(false);
    } catch (e) {
      console.error('Error adding digitization access: ', e);
      Notify.error({
        message: 'An error occurred while adding digitization access',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const isDisabled = useMemo<boolean>(() => {
    const p = projectsTable.getSelectedRowModel().rowsById;
    const o = orgsTable.getSelectedRowModel().rowsById;

    if (isEmpty(p) && isEmpty(o)) return true;
    return false;
  }, [projectsTable.getSelectedRowModel().rowsById, orgsTable.getSelectedRowModel().rowsById]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="agency-digitization-add-access">Add Access</Button>
      </DialogTrigger>
      <DialogContent
        title="Add Digitization Access"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="agency-digitization-add-access-dialog"
      >
        <DialogBody className="text-sm">
          <div className="flex h-[75vh] w-[75vw] max-w-6xl flex-col justify-between overflow-y-clip">
            <div className="relative flex grow justify-between overflow-y-auto">
              <ColumnContainer className="border-r pr-2">
                <Form onSubmit={projectsForm.handleSubmit(onSearchProjects)}>
                  <ColumnTop query={projectsQuery} text="Projects" isDisabled={!projectsForm.formState.isDirty} />

                  <ControlledInput
                    control={projectsForm.control}
                    label="Database ID"
                    name="id"
                    placeholder="Ex: 66fc5eb3c2d480c70976e850"
                    tooltipText="Search by the database ID"
                  />

                  <ControlledInput
                    control={projectsForm.control}
                    label="Project ID"
                    name="projectId"
                    placeholder="Ex: W2UDJA"
                    tooltipText="Search by the 6-digit project ID"
                  />

                  <ControlledInput
                    control={projectsForm.control}
                    label="Name"
                    name="name"
                    placeholder="Ex: 2026 Main Replacement"
                    tooltipText="Search by the project name"
                  />
                </Form>

                <SearchTable id="projectsTable" table={projectsTable} query={projectsQuery} />
              </ColumnContainer>

              <ColumnContainer className="pl-2">
                <Form onSubmit={orgsForm.handleSubmit(onSearchOrgs)}>
                  <ColumnTop text="Organizations" query={orgsQuery} isDisabled={!orgsForm.formState.isDirty} />

                  <ControlledInput
                    control={orgsForm.control}
                    label="Database ID"
                    name="id"
                    placeholder="Ex: 66fc5eb3c2d480c70976e850"
                    tooltipText="Search by the database ID"
                  />

                  <ControlledInput
                    control={orgsForm.control}
                    label="Organization Name"
                    name="name"
                    placeholder="Ex: CivilGrid (Staging)"
                    tooltipText="Search by the organization name"
                  />
                </Form>

                <SearchTable id="orgsTable" table={orgsTable} query={orgsQuery} />
              </ColumnContainer>
            </div>

            <DialogFooter className="mt-4 px-0 pb-0 pt-4">
              <Button
                onClick={onSubmit}
                loading={isProcessing}
                disabled={isDisabled}
                data-testid="agency-digitization-add-access-submit"
              >
                Submit
              </Button>
            </DialogFooter>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

interface SearchTableProps<T> {
  table: ReactTable<T>;
  query: TQuery;
  id: string;
}

function SearchTable<T = any>({ table, query, id }: SearchTableProps<T>) {
  if (query.loading) {
    return (
      <div className="flex w-full grow items-center justify-center">
        <CircularProgress indeterminate />
      </div>
    );
  }
  if (query.error) {
    return <div>An error occurred during data query.</div>;
  }
  return (
    <div className="mt-2 w-full grow overflow-y-auto">
      {!table.getRowCount() ? <div>No results found for the search criteria.</div> : <Table id={id} table={table} />}
    </div>
  );
}

interface ColumnTopProps {
  query: TQuery;
  text: string;
  isDisabled?: boolean;
}

const ColumnTop = ({ text, query, isDisabled }: ColumnTopProps) => {
  return (
    <div className="flex w-full items-start justify-between">
      <div className="text-base font-medium capitalize">{text}</div>

      <Button variant="secondary" type="submit" loading={query.loading} disabled={query.loading || isDisabled}>
        Search {text}
      </Button>
    </div>
  );
};

interface ColumnContainerProps {
  children?: React.ReactNode;
  className?: string;
}

const ColumnContainer = ({ children, className }: ColumnContainerProps) => {
  return <div className={classNames('relative flex w-1/2 flex-col overflow-y-auto', className)}>{children}</div>;
};
